import {
  BookMarked,
  MapPin,
  SearchCheck,
  Shirt,
  ArrowLeftRight,
  ShieldCheck,
} from "lucide-react";

const features = [
  {
    icon: BookMarked,
    eyebrow: "01 / Catalog",
    title: "Every piece, properly recorded.",
    body:
      "Brand, size, color, condition, material, client, photos and custom couture-specific fields — built for fashion, not generic assets.",
  },
  {
    icon: MapPin,
    eyebrow: "02 / Locate",
    title: "Know where everything is, always.",
    body:
      "Current location plus full movement history. Studio rack, archive bin, on a client, on loan — never guess again.",
  },
  {
    icon: SearchCheck,
    eyebrow: "03 / Find",
    title: "Find anything in seconds.",
    body:
      "Filter across every field at once. 'Cream silk, size 38, in studio, last seen this month' — done.",
  },
  {
    icon: Shirt,
    eyebrow: "04 / Pull",
    title: "Pull looks for fittings & shoots.",
    body:
      "Build outfits and lookbooks for fittings, editorial, red carpet. Share a clean view with the client or talent.",
  },
  {
    icon: ArrowLeftRight,
    eyebrow: "05 / Loans & returns",
    title: "Track loans so nothing walks off.",
    body:
      "Out-dates, return dates, who has it, condition on return. The receipts you wish you'd kept on the last shoot.",
  },
  {
    icon: ShieldCheck,
    eyebrow: "06 / Protect",
    title: "Protect condition and value.",
    body:
      "Garment care notes, maintenance log, and inventory value reporting — for insurance, audits, and peace of mind.",
  },
];

export default function Features() {
  return (
    <section
      id="features"
      data-testid="features-section"
      aria-labelledby="features-heading"
      className="py-20 md:py-28 border-t border-border bg-bone/40 grain"
    >
      <div className="max-w-[1320px] mx-auto px-6 md:px-12">
        <div className="md:flex md:items-end md:justify-between gap-10 mb-14 md:mb-20">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-5">
              — What it does
            </p>
            <h2
              id="features-heading"
              className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-balance"
            >
              The wardrobe room,{" "}
              <span className="italic">finally</span> running on something
              other than memory.
            </h2>
          </div>
          <p className="mt-6 md:mt-0 text-sm md:text-base text-muted-foreground max-w-sm leading-relaxed">
            Six tools, built around how stylists actually work — pulls,
            fittings, returns, archives, and the long memory of a serious
            collection.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {features.map((f, i) => (
            <article
              key={f.title}
              data-testid={`feature-${i}`}
              className="bg-background p-8 lg:p-10 hover:bg-ivory transition-colors group"
            >
              <div className="flex items-center justify-between mb-8">
                <span className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground">
                  {f.eyebrow}
                </span>
                <f.icon
                  className="w-5 h-5 text-oxblood transition-transform group-hover:-rotate-6"
                  strokeWidth={1.5}
                />
              </div>
              <h3 className="font-display text-xl md:text-2xl leading-snug">
                {f.title}
              </h3>
              <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                {f.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
